import { useState, useEffect } from "react";
import { Button, Col, Form, Input, Row, Select, Switch } from "antd";
import { Uploader } from "../../../../components/Upload";
import { useParams } from "react-router-dom";
import { useGetData } from "../../../../Network/useGetQueryData";
import { Loading } from "../../../../components/Loading";
import { maxLength, required } from "../../../../utils/form-validations";
import { useTranslation } from "react-i18next";

export const DetailsStep = ({
  onChangeStep,
  setFormData,
  formData,
}: {
  onChangeStep: (step: number) => void;
  setFormData: (data: any) => void;
  formData?: any;
}) => {
  const { i18n, t } = useTranslation();
  const industryList = [
    { label: t("industry.technology"), value: "technology" },
    { label: t("industry.healthcare"), value: "healthcare" },
    { label: t("industry.finance"), value: "finance" },
    { label: t("industry.manufacturing"), value: "manufacturing" },
    { label: t("industry.education"), value: "education" },
    { label: t("industry.retail"), value: "retail" },
    { label: t("industry.construction"), value: "construction" },
    { label: t("industry.other"), value: "other" },
  ];

  const companySizes = [
    { label: t("companySize.1_10"), value: "1-10" },
    { label: t("companySize.11_50"), value: "11-50" },
    { label: t("companySize.51_200"), value: "51-200" },
    { label: t("companySize.201_500"), value: "201-500" },
    { label: t("companySize.500_plus"), value: "500+" },
  ];

  const accountTypes = [
    { label: t("accountType.club"), value: "club" },
    { label: t("accountType.company"), value: "company" },
    { label: t("accountType.individual"), value: "individual" },
    { label: t("accountType.organization"), value: "organization" },
    { label: t("accountType.school"), value: "school" },
    { label: t("accountType.university"), value: "university" },
  ];

  const { id } = useParams();
  const [imgUrl, setImgUrl] = useState<string>();

  const { data, isLoading } = useGetData<{
    data: any;
  }>({
    queryKey: ["sponsor-details"],
    url: `sponsor/${id}`,
    options: {
      enabled: !!id,
      queryKey: ["sponsor-details"],
    },
  });

  const [form] = Form.useForm();

  useEffect(() => {
    if (!data || !id) return;

    const sponsorDetails = data?.data;

    form.setFieldsValue({
      ...sponsorDetails,
      contactPerson: {
        ...sponsorDetails.contactPerson,
        phoneNumber: sponsorDetails.contactPerson
          ? sponsorDetails.contactPerson.phoneNumber
          : sponsorDetails.phoneNumber,
      },
    });
    setFormData(sponsorDetails);
    sponsorDetails?.logo && setImgUrl(sponsorDetails?.logo);
  }, [data]);

  const handleAddSponsorsDetails = (values: any) => {
    const sponsorData = {
      ...values,
      logo: imgUrl,
      address: !!values?.address ? values?.address : undefined,
      websiteUrl: !!values?.websiteUrl ? values?.websiteUrl : undefined,
      contactPerson: { ...values.contactPerson, countryCode: "+20" },
      membershipPlanId: 7,
    };
    setFormData({ ...formData, ...sponsorData });
    onChangeStep(1);
  };

  if (isLoading) return <Loading />;

  return (
    <Form
      form={form}
      className={id ? "edit-view" : ""}
      layout="vertical"
      initialValues={{ status: "active" }}
    >
      <p className="form-section-title">{t("form.accountDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.companyName")}
            name={["name", "en"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.companyNameArabic")}
            name={["name", "ar"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(3000)]}
            label={t("form.description")}
            name={["description", "en"]}
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(3000)]}
            label={t("form.descriptionArabic")}
            name={["description", "ar"]}
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("form.industry")}
            rules={[required]}
            name="industry"
          >
            <Select
              showSearch
              placeholder={t("form.industry")}
              options={industryList}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("companies.companySize")}
            rules={[required]}
            name="companySize"
          >
            <Select
              showSearch
              placeholder={t("companies.companySize")}
              options={companySizes}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("companies.companyType")}
            rules={[required]}
            name="type"
          >
            <Select
              showSearch
              placeholder={t("companies.companyType")}
              options={accountTypes}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.accountStatus")} name="isActive">
            <Switch defaultChecked />
          </Form.Item>
        </Col>

        <Col lg={24} xl={12}>
          <Form.Item label={t("form.logo")} name="logo">
            {imgUrl ? (
              <div className="logo-view-details">
                <img
                  src={imgUrl}
                  alt="logo-url"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/assets/providers/mokhtabar.png";
                  }}
                />
                <span
                  onClick={() => setImgUrl(undefined)}
                  className="update-img-link"
                >
                  x
                </span>
              </div>
            ) : (
              <Uploader onChange={setImgUrl} />
            )}
          </Form.Item>
        </Col>
      </Row>
      <p className="form-section-title">{t("form.companyDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              { len: 10, message: t("validation.phoneLength") },
            ]}
            label={t("form.phoneNumber")}
            name="phoneNumber"
          >
            <Input prefix="+20" type="number" />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              maxLength(255),
              { type: "email", message: t("validation.email") },
            ]}
            label={t("form.email")}
            name="email"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.address")} name="address">
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              maxLength(3000),
              {
                pattern: /^www\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message: t("validation.websiteFormat"),
              },
            ]}
            label={t("form.websiteUrl")}
            name="websiteUrl"
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
      </Row>

      <p className="form-section-title">{t("form.contactPersonDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.name")}
            name={["contactPerson", "name"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              { len: 10, message: t("validation.phoneLength") },
            ]}
            name={["contactPerson", "phoneNumber"]}
            label={t("form.phoneNumber")}
          >
            <Input prefix="+20" type="number" />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              maxLength(255),
              { type: "email", message: t("validation.email") },
            ]}
            label={t("form.email")}
            name={["contactPerson", "email"]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <p className="form-section-title">{t("form.otherDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.commercialRegister")} name="cr">
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.taxId")}
            name="taxId"
          >
            <Input />
          </Form.Item>
        </Col>

        <Col lg={24} xl={12}>
          <Form.Item label={t("form.bank")} name="bankName">
            <Select
              showSearch
              options={[
                { label: "CIB", value: "CIB" },
                { label: "QNB", value: "QNB" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("form.bankAccount")}
            name="bankAccountNumber"
            rules={[{ max: 255, message: t("validation.maxLength255") }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.bankIban")} name="bankIban">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <div className="steps-action-footer">
        <Button
          type="primary"
          style={{ marginRight: "8px" }}
          onClick={async () => {
            try {
              const values = await form.validateFields();
              handleAddSponsorsDetails(values);
            } catch (error) {}
          }}
        >
          {t("form.next")}
        </Button>
      </div>
    </Form>
  );
};
